import {
  Avatar,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  IconButton,
  useDisclosure,
} from '@chakra-ui/react';
import {colors, useCustomerContext, useOrderContext} from 'components/providers';
import {UserOrdersModal} from 'components/shared';
import {MenuItemBody} from 'components/shared/profile/components/profile-menu-list/components/menu-item-body';
import {profileItems} from 'components/shared/profile/utils';
import {useAuth, useDesktop, useTranslation} from 'hooks';
import {FC, useRef} from 'react';
import {modalCloseButtonStyles} from 'theme';
import NextImageUI from 'ui/NextImage';

import {LanguageSelector} from '../language-selector';
import {NotificationsBell} from '../notifications-bell';
import {OrdersMobileWithNotification} from './components/orders-mobile-with-notifications';

interface IProps {}

export const SidebarMobileMenu: FC<IProps> = () => {
  const {isAuthenticated, redirectToLoginPage, logout} = useAuth();
  const {t} = useTranslation();
  const {isOpen: isOpenDrawer, onOpen: onOpenDrawer, onClose: onCloseDrawer} = useDisclosure();
  const {isOpen: isOpenModal, onOpen: onOpenModal, onClose: onCloseModal} = useDisclosure();
  const {customer} = useCustomerContext();
  const isDesktop = useDesktop();
  const {ordersActive, orders, onHandlerSetLimit, isLoadingOrders} = useOrderContext();

  const btnRef = useRef<HTMLButtonElement>(null);
  const orderCount = ordersActive.length ?? 0;

  const handleOrdersClick = () => {
    onCloseDrawer();
    onOpenModal();
  };

  const ordersObj = {
    icon: '/profile-menu/orders.svg',
    title: 'ORDERS',
    onClick: handleOrdersClick,
    needAuth: true,
  };

  const sidebarProfileItems = [...profileItems];

  if (!isAuthenticated) {
    sidebarProfileItems.push({
      icon: '/profile-menu/login.svg',
      title: 'LOGIN_TITLE',
      name: 'login',
      onClick: redirectToLoginPage,
      needAuth: false,
    });
  }

  return (
    <Box position="relative">
      {isDesktop ? (
        <Box as="button" onClick={onOpenDrawer}>
          <Avatar
            bg={colors.lightGray}
            icon={<NextImageUI src="/user_online.svg" alt="User" width={24} height={24} priority />}
          />
        </Box>
      ) : (
        <IconButton
          onClick={onOpenDrawer}
          aria-label="Options"
          bg={colors.lightGray}
          position="relative"
          _focus={{border: 'none'}}
          icon={
            <NextImageUI
              width={20}
              height={20}
              src="/profile-menu/burger-menu-icon.svg"
              alt="Menu"
              // width or height modified, but not the other. If you use CSS to change the size of your image, also include the styles 'width: "auto"' or 'height: "auto"' to maintain the aspect ratio.
              style={{width: '20px', height: '20px'}}
            />
          }
          ml="10px"
          h="45px"
        />
      )}

      {orderCount > 0 && <NotificationsBell boxProps={{right: '0px', top: '0px'}} count={orderCount} />}
      <Drawer isOpen={isOpenDrawer} placement="right" onClose={onCloseDrawer} finalFocusRef={btnRef}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton sx={modalCloseButtonStyles} />
          <DrawerHeader pb={0}>
            {`${t('MOBILE_SIDE_BAR_TITLE')}${customer?.name ? ',' : ''} ${customer?.name ?? ''}!`}
          </DrawerHeader>
          <DrawerBody>
            <OrdersMobileWithNotification {...ordersObj} orderCount={orderCount} isLoggedIn={isAuthenticated} />
            {!isDesktop && (
              <Box w="100%" py="10px">
                <LanguageSelector isDesktop={false} onChangeLanguage={onCloseDrawer} accordionMobileSize="100%" />
              </Box>
            )}
            {sidebarProfileItems.map((item, index) => {
              if (item.needAuth && !isAuthenticated) return null;
              if (item.name === 'logout') {
                item.onClick = () => {
                  logout();
                };
              }

              return <MenuItemBody key={index} {...item} />;
            })}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      {isOpenModal && orders && (
        <UserOrdersModal
          orders={orders}
          isLoadingOrders={isLoadingOrders}
          onHandlerSetLimit={onHandlerSetLimit}
          isOpen={isOpenModal}
          onClose={onCloseModal}
        />
      )}
    </Box>
  );
};
